import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  FileText,
  Plus,
  Star,
  Trash2,
  Loader2,
  ChevronRight,
  AlertTriangle,
  MessageSquare,
  Target,
  HelpCircle,
  Shield,
  ArrowLeft,
  Download,
  Clock,
  Zap,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CALL_TYPES = [
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'demo', label: 'Demo' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'follow_up', label: 'Follow-up' },
];

// Script section renderer
function ScriptSection({ icon: Icon, title, children, color = 'text-primary' }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
      </div>
      <div className="pl-6">{children}</div>
    </div>
  );
}

// Script viewer component
function ScriptViewer({ script, isImprovement }) {
  const s = script;
  return (
    <div className="space-y-6" data-testid="script-viewer">
      <ScriptSection icon={MessageSquare} title="Opening">
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{s.opening}</p>
      </ScriptSection>

      <ScriptSection icon={HelpCircle} title="Discovery Questions" color="text-amber-600">
        <ol className="space-y-2 list-decimal list-inside">
          {s.discovery_questions?.map((q, i) => (
            <li key={`k1-${i}`} className="text-sm text-foreground">{q}</li>
          ))}
        </ol>
      </ScriptSection>

      <ScriptSection icon={Target} title="Qualification" color="text-emerald-600">
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{s.qualification}</p>
      </ScriptSection>

      <ScriptSection icon={Zap} title="Value Pitch" color="text-orange-600">
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{s.value_pitch}</p>
      </ScriptSection>

      <ScriptSection icon={Shield} title="Objection Handling" color="text-red-600">
        <div className="space-y-3">
          {s.objection_handling?.map((o, i) => (
            <div key={`k2-${i}`} className="p-3 rounded-lg bg-secondary/30 border border-border/50">
              <p className="text-xs font-medium text-red-600 mb-1">"{o.objection}"</p>
              <p className="text-sm text-foreground">{o.response}</p>
            </div>
          ))}
        </div>
      </ScriptSection>

      <ScriptSection icon={ChevronRight} title="Closing">
        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{s.closing}</p>
      </ScriptSection>

      <ScriptSection icon={AlertTriangle} title="Key Risks" color="text-destructive">
        <ul className="space-y-1.5">
          {s.key_risks?.map((r, i) => (
            <li key={`k3-${i}`} className="text-sm text-muted-foreground flex items-start gap-2">
              <span className="text-destructive mt-0.5 shrink-0">!</span>
              {r}
            </li>
          ))}
        </ul>
      </ScriptSection>

      {isImprovement && s.improvements?.length > 0 && (
        <ScriptSection icon={Zap} title="Improvements Made" color="text-emerald-600">
          <div className="space-y-3">
            {s.improvements.map((imp, i) => (
              <div key={`k4-${i}`} className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                <p className="text-xs font-medium text-red-600">Weak: {imp.what_was_weak}</p>
                <p className="text-xs font-medium text-emerald-600 mt-1">Changed: {imp.what_changed}</p>
                <p className="text-xs text-muted-foreground mt-1">Why: {imp.why}</p>
              </div>
            ))}
          </div>
        </ScriptSection>
      )}
    </div>
  );
}

export default function ScriptBuilder() {
  const [view, setView] = useState('list'); // list | form | detail
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [calls, setCalls] = useState([]);

  // Form state
  const [product, setProduct] = useState('');
  const [icp, setIcp] = useState('');
  const [industry, setIndustry] = useState('');
  const [callType, setCallType] = useState('discovery');
  const [goal, setGoal] = useState('');
  const [existingScript, setExistingScript] = useState('');
  const [selectedCallId, setSelectedCallId] = useState('');

  const fetchScripts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/scripts`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setScripts(data.scripts || []);
      }
    } catch (e) {
      console.error('Failed to fetch scripts:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalls = async () => {
    try {
      const res = await fetch(`${API_URL}/api/scripts/calls/list`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCalls(data.calls || []);
      }
    } catch (e) {
      console.error('Failed to fetch calls:', e);
    }
  };

  useEffect(() => {
    fetchScripts();
    fetchCalls();
  }, []);

  const handleGenerate = async () => {
    if (!product.trim() || !icp.trim() || !goal.trim()) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/scripts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          product: product.trim(),
          icp: icp.trim(),
          industry: industry.trim(),
          call_type: callType,
          goal: goal.trim(),
          existing_script: existingScript.trim() || null,
          call_id: selectedCallId || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedScript(data);
        setView('detail');
        fetchScripts();
        resetForm();
      }
    } catch (e) {
      console.error('Failed to generate script:', e);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (scriptId) => {
    if (!window.confirm('Delete this script?')) return;
    try {
      await fetch(`${API_URL}/api/scripts/${scriptId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setScripts(prev => prev.filter(s => s.script_id !== scriptId));
      if (selectedScript?.script_id === scriptId) {
        setView('list');
        setSelectedScript(null);
      }
    } catch (e) {
      console.error('Failed to delete:', e);
    }
  };

  const handleToggleFavorite = async (scriptId, current) => {
    try {
      await fetch(`${API_URL}/api/scripts/${scriptId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_favorite: !current }),
      });
      setScripts(prev =>
        prev.map(s => s.script_id === scriptId ? { ...s, is_favorite: !current } : s)
      );
    } catch (e) {
      console.error('Failed to toggle favorite:', e);
    }
  };

  const resetForm = () => {
    setProduct('');
    setIcp('');
    setIndustry('');
    setCallType('discovery');
    setGoal('');
    setExistingScript('');
    setSelectedCallId('');
  };

  const openScript = async (scriptId) => {
    try {
      const res = await fetch(`${API_URL}/api/scripts/${scriptId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSelectedScript(data);
        setView('detail');
      }
    } catch (e) {
      console.error('Failed to fetch script:', e);
    }
  };

  const copyScriptText = () => {
    if (!selectedScript?.script) return;
    const s = selectedScript.script;
    let text = `# ${s.title || selectedScript.title}\n\n`;
    text += `## Opening\n${s.opening}\n\n`;
    text += `## Discovery Questions\n${s.discovery_questions?.map((q, i) => `${i + 1}. ${q}`).join('\n')}\n\n`;
    text += `## Qualification\n${s.qualification}\n\n`;
    text += `## Value Pitch\n${s.value_pitch}\n\n`;
    text += `## Objection Handling\n${s.objection_handling?.map(o => `> "${o.objection}"\n${o.response}`).join('\n\n')}\n\n`;
    text += `## Closing\n${s.closing}\n\n`;
    text += `## Key Risks\n${s.key_risks?.map(r => `- ${r}`).join('\n')}\n`;
    navigator.clipboard.writeText(text);
  };

  // LIST VIEW
  if (view === 'list') {
    return (
      <Layout>
        <div className="space-y-6" data-testid="script-builder-page">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold">Script Builder</h1>
              <p className="text-sm text-muted-foreground mt-1">AI-powered sales scripts tailored to your calls</p>
            </div>
            <Button onClick={() => setView('form')} data-testid="new-script-btn">
              <Plus className="w-4 h-4 mr-2" /> New Script
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : scripts.length === 0 ? (
            <Card className="bg-card border-border/50">
              <CardContent className="p-12 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No scripts yet</h3>
                <p className="text-sm text-muted-foreground mb-4">Create your first AI-generated sales script</p>
                <Button onClick={() => setView('form')}>
                  <Plus className="w-4 h-4 mr-2" /> Create Script
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {scripts.map((s) => (
                <Card
                  key={s.script_id}
                  className="bg-card border-border/50 hover:border-primary/30 transition-colors cursor-pointer"
                  onClick={() => openScript(s.script_id)}
                  data-testid={`script-card-${s.script_id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <FileText className="w-5 h-5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{s.title}</p>
                        {s.is_improvement && (
                          <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-400/30">Improved</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-muted-foreground">{s.product}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {CALL_TYPES.find(t => t.value === s.call_type)?.label || s.call_type}
                        </Badge>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(s.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => { e.stopPropagation(); handleToggleFavorite(s.script_id, s.is_favorite); }}
                        data-testid={`fav-btn-${s.script_id}`}
                      >
                        <Star className={`w-4 h-4 ${s.is_favorite ? 'fill-yellow-400 text-amber-600' : 'text-muted-foreground'}`} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); handleDelete(s.script_id); }}
                        data-testid={`delete-btn-${s.script_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // FORM VIEW
  if (view === 'form') {
    return (
      <Layout>
        <div className="space-y-6" data-testid="script-form">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setView('list')} data-testid="back-to-list">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-bold">New Script</h1>
              <p className="text-sm text-muted-foreground">Fill in the details to generate your script</p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Left: Required inputs */}
            <div className="space-y-4">
              <Card className="bg-card border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Script Parameters</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Product *</label>
                    <Input
                      placeholder="e.g. CRM Software, Project Management Tool"
                      value={product}
                      onChange={(e) => setProduct(e.target.value)}
                      data-testid="input-product"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Ideal Customer Profile (ICP) *</label>
                    <Input
                      placeholder="e.g. Sales managers at B2B companies, 10-500 employees"
                      value={icp}
                      onChange={(e) => setIcp(e.target.value)}
                      data-testid="input-icp"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Industry</label>
                    <Input
                      placeholder="e.g. SaaS, Healthcare, Financial Services"
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      data-testid="input-industry"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Call Type</label>
                      <Select value={callType} onValueChange={setCallType}>
                        <SelectTrigger data-testid="select-call-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CALL_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Import Insights</label>
                      <Select value={selectedCallId} onValueChange={setSelectedCallId}>
                        <SelectTrigger data-testid="select-call-insights">
                          <SelectValue placeholder="Select a call..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No insights</SelectItem>
                          {calls.map(c => (
                            <SelectItem key={c.call_id} value={c.call_id}>
                              {c.company_name || c.title || c.call_id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Goal *</label>
                    <Input
                      placeholder="e.g. Book a product demo, Qualify the lead"
                      value={goal}
                      onChange={(e) => setGoal(e.target.value)}
                      data-testid="input-goal"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right: Optional existing script */}
            <div className="space-y-4">
              <Card className="bg-card border-border/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    Existing Script
                    <Badge variant="outline" className="text-[10px]">Optional</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Paste an existing script here to improve it based on call insights..."
                    value={existingScript}
                    onChange={(e) => setExistingScript(e.target.value)}
                    className="min-h-[280px] font-mono text-sm"
                    data-testid="input-existing-script"
                  />
                  <p className="text-[10px] text-muted-foreground mt-2">
                    {existingScript ? 'AI will improve this script based on your inputs and call insights.' : 'Leave empty to generate a new script from scratch.'}
                  </p>
                </CardContent>
              </Card>

              <Button
                className="w-full"
                disabled={generating || !product.trim() || !icp.trim() || !goal.trim()}
                onClick={handleGenerate}
                data-testid="generate-script-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating script...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4 mr-2" />
                    {existingScript ? 'Improve Script' : 'Generate Script'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && selectedScript) {
    const s = selectedScript.script || selectedScript;
    return (
      <Layout>
        <div className="space-y-6" data-testid="script-detail">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { setView('list'); setSelectedScript(null); }} data-testid="back-from-detail">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="font-heading text-xl font-bold">{selectedScript.title || s.title}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[10px]">
                    {CALL_TYPES.find(t => t.value === selectedScript.call_type)?.label || selectedScript.call_type}
                  </Badge>
                  {selectedScript.is_improvement && (
                    <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-400/30">Improved</Badge>
                  )}
                  {selectedScript.product && (
                    <span className="text-xs text-muted-foreground">{selectedScript.product}</span>
                  )}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={copyScriptText} data-testid="copy-script-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Copy
            </Button>
          </div>

          <Card className="bg-card border-border/50">
            <CardContent className="p-6">
              <ScrollArea className="max-h-[600px]">
                <ScriptViewer script={s} isImprovement={selectedScript.is_improvement} />
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return null;
}
